/**
 * taskLightboxStore — Zustand store for the task-detail lightbox (LEV-1046).
 *
 * Clicking a task (in the task list, or "Add from Apollo" for an agent task)
 * opens the lightbox; it mounts at the canvas level (OwnerCanvas) so it shares
 * the app's lightbox orchestration — LevAI-panel + task-list coordination,
 * z-index/stacking, positioning — exactly like CanvasLightbox / PeopleDbLightbox.
 * This store is how a deep trigger and the canvas-level mount agree on which
 * task is open, mirroring `sidebarPanelStore` for the People DB lightbox.
 *
 * Optimistic open: flows that CREATE the task server-side (prospecting.start
 * takes ~5s) call `openPendingTask()` before firing the mutation so the
 * lightbox appears instantly, then `resolvePendingTask(actionId)` on success —
 * which is a no-op if the user already closed it — or `cancelPendingTask()` on
 * failure (which never closes a non-pending lightbox the user opened since).
 *
 * `actionId` lingers after `close()` so the lightbox can run its exit animation;
 * the next `open()` overwrites it.
 */
import { create } from "zustand";
import type { TaskSurface } from "@/lib/analytics/taskSurface";

export interface TaskLightboxState {
  /** The task whose lightbox is open (or was last open, during exit animation). */
  actionId: string | null;
  isOpen: boolean;
  /** Optimistic open — the task is still being created server-side. */
  pending: boolean;
  /** Placeholder title shown until the pending task resolves and loads. */
  pendingTitle: string | null;
  /**
   * Activity-feed entry to scroll to + highlight once the task loads — set by a
   * "tagged you in a comment" notification deep link (`?taskComment=<entryId>`).
   * The view consumes it on render and calls `clearFocusEntry` so a later
   * manual reopen of the same task doesn't re-scroll.
   */
  focusEntryId: string | null;
  /**
   * The surface that opened the lightbox, reported on its `task_marked_read`
   * card-view event. Every open overwrites it, so it never carries a previous
   * opener's surface into the next task.
   */
  surface: TaskSurface;
  openTask: (actionId: string, surface: TaskSurface, focusEntryId?: string | null) => void;
  openPendingTask: (placeholder?: { title?: string }) => void;
  resolvePendingTask: (actionId: string) => void;
  cancelPendingTask: () => void;
  closeTask: () => void;
  clearFocusEntry: () => void;
}

export const useTaskLightboxStore = create<TaskLightboxState>((set) => ({
  actionId: null,
  isOpen: false,
  pending: false,
  pendingTitle: null,
  focusEntryId: null,
  surface: "unknown",
  openTask: (actionId, surface, focusEntryId = null) =>
    set({ actionId, isOpen: true, pending: false, pendingTitle: null, focusEntryId, surface }),
  // The only pending opener is the pipeline board's prospecting CTA, which is
  // none of the task surfaces.
  openPendingTask: (placeholder) =>
    set({
      actionId: null,
      isOpen: true,
      pending: true,
      pendingTitle: placeholder?.title ?? null,
      focusEntryId: null,
      surface: "unknown",
    }),
  resolvePendingTask: (actionId) =>
    set((s) => {
      if (!s.pending) return {};
      // User closed the pending lightbox before the server returned — don't
      // reopen it; the task is reachable from the task list.
      if (!s.isOpen) return { pending: false, pendingTitle: null };
      // pendingTitle is DELIBERATELY kept on resolve: the view renders it as
      // the title fallback until the task query loads, so the header doesn't
      // flash a generic "Task" between resolve and data arrival. openTask /
      // closeTask clear it.
      return { actionId, pending: false };
    }),
  cancelPendingTask: () =>
    set((s) =>
      s.pending ? { isOpen: false, pending: false, pendingTitle: null } : {},
    ),
  closeTask: () =>
    set({ isOpen: false, pending: false, pendingTitle: null, focusEntryId: null }),
  clearFocusEntry: () => set({ focusEntryId: null }),
}));
